import type { SetCombo } from './engine';
import {
  FAMILIES, type AnySet, type Family, type Plan, type PlanStatus, type Project,
} from './types';

export const FAMILY_KEYS = FAMILIES.map((f) => f.key);
export const familyLabel = (k: Family): string => FAMILIES.find((f) => f.key === k)!.label;

export const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Les 6 jeux (1 par famille) référencés par un plan ; null si l'un a été supprimé. */
export function setsOf(project: Pick<Project, 'sets'>, plan: Plan): SetCombo | null {
  const out = {} as Record<Family, AnySet | undefined>;
  for (const k of FAMILY_KEYS) out[k] = (project.sets[k] as AnySet[]).find((s) => s.id === plan.setIds[k]);
  if (FAMILY_KEYS.some((k) => !out[k])) return null;
  return out as unknown as SetCombo;
}

/** Nom du jeu courant référencé par un plan dans une famille (ou « (supprimé) »). */
export const setNameInPlan = (project: Project, plan: Plan, k: Family): string =>
  (project.sets[k] as AnySet[]).find((s) => s.id === plan.setIds[k])?.name ?? '(supprimé)';

/** Raisons d'obsolescence : un jeu modifié / remplacé / supprimé depuis le snapshot du calcul. */
export function staleReasons(project: Project, plan: Plan): string[] {
  const snap = plan.snapshot;
  if (!snap) return plan.invalidations ?? [];
  const reasons: string[] = [...(plan.invalidations ?? [])];
  if (plan.configurationChanged) reasons.push('Bâtiments, tracé, phasage ou hypothèses choisies modifiés depuis le calcul');
  for (const { key, label } of FAMILIES) {
    const cur = (project.sets[key] as AnySet[]).find((s) => s.id === plan.setIds[key]);
    const was = snap.sets[key];
    if (!cur) reasons.push(`${label} : le jeu utilisé a été supprimé`);
    else if (cur.id !== was.id) reasons.push(`${label} : jeu remplacé par « ${cur.name} »`);
    else if (cur.version !== was.version) reasons.push(`${label} : « ${cur.name} » modifié (v${was.version} → v${cur.version})`);
  }
  return reasons;
}

export const planStatus = (project: Project, plan: Plan): PlanStatus => {
  if (plan.status === 'running' || plan.status === 'failed') return plan.status;
  if (staleReasons(project, plan).length) return 'stale';
  return plan.buildingsConfirmed && plan.traceCalculated && plan.densityCalculated ? 'done' : 'draft';
};

/** Un plan est-il à jour vis-à-vis d'un jeu précis (famille) ? Sert au badge par-jeu. */
export function planFreshForSet(plan: Plan, family: Family, set: AnySet): 'fresh' | 'stale' | 'draft' {
  if (plan.status !== 'done' || !plan.snapshot) return 'draft';
  const was = plan.snapshot.sets[family];
  return was && was.id === set.id && was.version === set.version ? 'fresh' : 'stale';
}

export const plansUsing = (project: Project, family: Family, setId: string): Plan[] =>
  project.plans.filter((p) => p.setIds[family] === setId);

/** Résumé d'un jeu (une ligne). */
export function summarize(s: AnySet): string {
  switch (s.kind) {
    case 'demande':
      return `conso ${s.consoCoef} % · raccord. ${s.connectionRate} % · érosion ${s.erosion} %/an`;
    case 'trace':
      return `${s.forbiddenRoads} interdite(s) · ${s.mandatoryRoads} obligatoire(s) · ${s.weightedRoads} pondérée(s) · ${s.useExistingNetwork ? 'réseau existant' : 'réseau neuf'}`;
    case 'developpement':
      return `${s.targetKm} km · ${Math.round(s.targetMWh / 1000)} GWh · ${objectiveLabel(s.objective)}${s.otherObjective ? ` · ${s.otherObjective}` : ''}`;
    case 'phasage':
      return `${s.kmPerYear} km/an · ${Math.round(s.mwhPerYear / 1000)} GWh/an · ${s.clientsPerYear} clients/an`;
    case 'importance':
      return `${s.mandatoryBuildings.length} obligatoire(s) · densif. ${s.densification} · ${priorityLabel(s.priority)}`;
    case 'pointsDepart':
      return `${s.startPoints} départ(s) · chaufferies ${s.boilerRooms} · prod. ${s.commissioningYear} (${s.capacityMW} MW)`;
  }
}

export const objectiveLabel = (o: 'km' | 'mwh' | 'mixte') =>
  o === 'km' ? 'objectif km' : o === 'mwh' ? 'objectif MWh' : 'objectif mixte';
export const priorityLabel = (p: 'obligatoire' | 'densification' | 'mixte') =>
  p === 'obligatoire' ? 'priorité obligatoires' : p === 'densification' ? 'priorité densification' : 'priorité mixte';
